import { Component, ViewChild, effect, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyType } from 'src/app/models/company.model';
import { MasterAdminCompanyStore } from 'src/app/stores/master-admin-company.store';
import { countryBase, fieldsApplicationOptionsBase } from 'src/utils/data';
import { OptionsCountry } from 'src/app/components/profile-form/profile-form.component';
import { ApplicationsOptionsInputComponent } from 'src/app/components/inputs/applications-options-input/applications-options-input.component';
import { CompanyInputComponent } from 'src/app/components/inputs/company-input/company-input.component';
import { CountryOptionsInputComponent } from 'src/app/components/inputs/country-options-input/country-options-input.component';
import { IndustryOptionsInputComponent } from 'src/app/components/inputs/industry-options-input/industry-options-input.component';
import { PostalcodeInputComponent } from 'src/app/components/inputs/postalcode-input/postalcode-input.component';
import { StateOrProvinceInputComponent } from 'src/app/components/inputs/state-or-province-input/state-or-province-input.component';

type FormAction = 'create' | 'update' | null;

@Component({
  selector: 'app-master-admin-company-form',
  templateUrl: './master-admin-company-form.component.html',
  styleUrls: ['./master-admin-company-form.component.scss'],
  standalone: false,
})
export class MasterAdminCompanyFormComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  protected readonly companyStore = inject(MasterAdminCompanyStore);
  countryOptions: OptionsCountry[] = countryBase;
  fieldsApplicationOptions = fieldsApplicationOptionsBase;
  applicationOptions: string[] = [];
  @ViewChild(ApplicationsOptionsInputComponent)
  applicationsOptionsInputComponent!: ApplicationsOptionsInputComponent;

  form: FormGroup = this.formBuilder.nonNullable.group({
    company: CompanyInputComponent.companyInputFormControl(),
    country: CountryOptionsInputComponent.countryOptionsGroup(),
    industry: IndustryOptionsInputComponent.industryOptionsGroup(),
    application: ApplicationsOptionsInputComponent.applicationOptionsGroup(),
    stateOrProvince: StateOrProvinceInputComponent.stateOrProvinceControl(),
    postalcode: PostalcodeInputComponent.postalcodeControl(),
  });

  editingCompanyId: string | null = null;
  showResponseBanner = false;
  successfulSave = false;
  successMessage = '';
  errorMessage = '';
  private pendingAction: FormAction = null;

  constructor() {
    effect(() => {
      const saving = this.companyStore.saving();
      const error = this.companyStore.error();

      if (!this.pendingAction || saving) {
        return;
      }

      if (error) {
        this.successfulSave = false;
        this.errorMessage = this.resolveErrorMessage(
          error,
          'Failed to save company.',
        );
        this.showResponseBanner = true;
        this.pendingAction = null;
        return;
      }

      this.successfulSave = true;
      this.errorMessage = '';
      this.successMessage = this.getSuccessMessage(this.pendingAction);
      this.showResponseBanner = true;
      this.pendingAction = null;
      this.companyStore.loadCompanies();
      this.navigateToList();
    });
  }

  ngOnInit(): void {
    this.companyStore.clearError();
    this.showResponseBanner = false;

    const companyId = this.route.snapshot.paramMap.get('id');
    if (!companyId) {
      this.form.reset();
      this.applicationOptions = [];
      return;
    }

    this.editingCompanyId = companyId;
    this.companyStore.loadCompany(companyId);
  }

  get isEditMode(): boolean {
    return !!this.editingCompanyId;
  }

  get currentCompany(): CompanyType | null {
    if (!this.isEditMode || !this.editingCompanyId) {
      return null;
    }
    const selectedCompany = this.companyStore.selectedCompany();
    if (!selectedCompany || selectedCompany.id !== this.editingCompanyId) {
      return null;
    }
    return selectedCompany as CompanyType;
  }

  get isFormReady(): boolean {
    return !this.isEditMode || this.currentCompany !== null;
  }

  submit(): void {
    this.markAllFieldsAsDirty();
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    const payload = this.mapFormToCompanyPayload();
    this.showResponseBanner = false;

    if (this.isEditMode && this.editingCompanyId) {
      payload.id = this.editingCompanyId;
      this.pendingAction = 'update';
      this.companyStore.updateCompany(this.editingCompanyId, payload);
      return;
    }

    this.pendingAction = 'create';
    this.companyStore.createCompany(payload);
  }

  cancelForm(): void {
    this.navigateToList();
  }

  changeOptions(options: string[]): void {
    this.applicationOptions = options;
  }

  markAllFieldsAsDirty(): void {
    Object.keys(this.form.controls).forEach((control) =>
      this.form.get(control)?.markAsDirty(),
    );
  }

  private navigateToList(): void {
    this.router.navigate(['/admin/companies']);
  }

  private mapFormToCompanyPayload(): CompanyType {
    const fieldsOfApplication =
      this.applicationsOptionsInputComponent?.getValues() ?? [];

    return {
      id: this.editingCompanyId ?? '',
      name: this.form.value.company,
      country: this.form.value.country.country,
      stateOrProvince: this.form.value.stateOrProvince,
      postalCode: this.form.value.postalcode,
      industry: this.form.value.industry.industry,
      fieldsOfApplication,
    };
  }

  private getSuccessMessage(action: Exclude<FormAction, null>): string {
    switch (action) {
      case 'create':
        return 'Company created successfully.';
      case 'update':
        return 'Company updated successfully.';
      default:
        return 'Operation successful.';
    }
  }

  private resolveErrorMessage(error: unknown, fallback: string): string {
    if (!error) {
      return fallback;
    }
    if (typeof error === 'string') {
      return error;
    }
    if (typeof error === 'object' && error !== null) {
      const typedError = error as {
        message?: string;
        error?: { message?: string; title?: string };
      };
      if (typedError.error?.message) {
        return typedError.error.message;
      }
      if (typedError.error?.title) {
        return typedError.error.title;
      }
      if (typedError.message) {
        return typedError.message;
      }
    }
    return fallback;
  }
}
